/**
 * HeroSection Component
 *
 * Hero banner section:
 * - Title (HeadingBlock)
 * - Subtitle (TextBlock or HeadingBlock)
 * - CTAButton (ButtonLink)
 *
 * Styling uses Bootstrap utility class props consistent with other sections.
 */
import React from "react";
import { filterClass } from "../../utils/class-utils";
import type { HeroSectionProps } from "../types";

export function HeroSection(props: HeroSectionProps) {
  const {
    Title,
    Subtitle,
    CTAButton,
    bgImage,
    bgImageAspectRatio = "auto",
    bgImageObjectFit = "cover",
    bgImageObjectPosition = "center",
    paddingYClass = "no-class",
    innerPaddingYClass = "no-class",
    innerBgClass = "no-class",
    bgClass = "no-class",
    contentAlignClass = "no-class",
    contentJustifyClass = "no-class",
  } = props;

  const hasBgImage = Boolean(bgImage);
  const useRatioContainer = hasBgImage && bgImageAspectRatio !== "auto";
  const supportsBootstrapRatio =
    bgImageAspectRatio === "1/1" || bgImageAspectRatio === "4/3" || bgImageAspectRatio === "16/9";
  const bgRatioClass =
    bgImageAspectRatio && supportsBootstrapRatio ? `ratio ratio-${bgImageAspectRatio.replace("/", "x")}` : "";
  const bgObjectFitClass = hasBgImage && bgImageObjectFit ? `object-fit-${bgImageObjectFit}` : "";
  const bgObjectPositionClass = hasBgImage && bgImageObjectPosition ? `object-position-${bgImageObjectPosition}` : "";

  const objectPositionCss =
    bgImageObjectPosition === "start"
      ? "left"
      : bgImageObjectPosition === "end"
        ? "right"
        : bgImageObjectPosition;

  const bgImageStyle: React.CSSProperties | undefined =
    bgImageObjectFit || objectPositionCss
      ? {
          objectFit: bgImageObjectFit,
          objectPosition: objectPositionCss,
        }
      : undefined;

  const sectionClassName = ["hero-section section", filterClass(bgClass)].filter(Boolean).join(" ").trim();
  const bgWrapperClassName = [
    hasBgImage ? "position-relative overflow-hidden" : "",
    useRatioContainer ? bgRatioClass : "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const bgImageClassName = [
    "position-absolute top-0 start-0 w-100 h-100",
    bgObjectFitClass,
    bgObjectPositionClass,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const contentLayerClassName = [
    hasBgImage ? "position-relative z-1" : "",
    useRatioContainer ? "position-absolute top-0 start-0 w-100 h-100" : "",
    filterClass(paddingYClass),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const innerwrapClassName = [
    "d-flex flex-column",
    useRatioContainer ? "h-100" : "",
    filterClass(contentAlignClass),
    filterClass(contentJustifyClass),
    filterClass(innerPaddingYClass),
    filterClass(innerBgClass),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={sectionClassName || undefined}>
      <div className={bgWrapperClassName || undefined}>
        {hasBgImage ? (
          <img
            src={bgImage}
            alt=""
            aria-hidden="true"
            className={bgImageClassName}
            style={bgImageStyle}
          />
        ) : null}
        <div className={contentLayerClassName || undefined}>
          <div className="container-xl">
            <div className={innerwrapClassName}>
              {Title && (
                <div>
                  <Title.type {...Title.props} />
                </div>
              )}
              {Subtitle && (
                <div className="mt-2">
                  <Subtitle.type {...Subtitle.props} />
                </div>
              )}
              {CTAButton && (
                <div className="mt-4 d-flex">
                  <div className="d-inline-block">
                    <CTAButton.type {...CTAButton.props} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;

